<template>
  <div
    v-if="src"
    class="br-image-side-zoom"
    :class="{ 'br-image-side-zoom--zoomed': zoomed }"
    :style="wrapStyle"
    @mousemove="onMove"
    @mouseleave="exitZoom"
  >
    <img
      v-show="!zoomed"
      ref="img"
      :src="src"
      :class="imgClass"
      class="br-image-side-zoom__img"
      draggable="false"
      alt=""
    />
  </div>
</template>

<script>
/**
 * 悬停放大：鼠标进入图像区域即放大，移动平移查看；移出还原。
 * 放大视口铺满父容器（可使用预览方框上下留白），背景按原图显示尺寸 × 倍率缩放，保持比例不拉伸。
 */
const ZOOM = 2.4;

export default {
  name: 'BrImageSideZoom',
  props: {
    src: { type: String, default: '' },
    imgClass: { type: String, default: '' },
    enabled: { type: Boolean, default: true },
  },
  data() {
    return {
      zoomed: false,
      lockW: 0,
      lockH: 0,
      /** 进入放大前图片实际显示宽高（object-contain 后） */
      baseW: 0,
      baseH: 0,
      bgPos: '50% 50%',
    };
  },
  computed: {
    wrapStyle() {
      if (!this.zoomed) {
        return {};
      }
      return {
        width: `${this.lockW}px`,
        height: `${this.lockH}px`,
        backgroundImage: `url("${String(this.src).replace(/"/g, '\\"')}")`,
        backgroundRepeat: 'no-repeat',
        // 按原图显示尺寸放大，避免铺满方框时把竖版证件拉成方形
        backgroundSize: `${this.baseW * ZOOM}px ${this.baseH * ZOOM}px`,
        backgroundPosition: this.bgPos,
      };
    },
  },
  watch: {
    src: 'exitZoom',
    enabled(v) {
      if (!v) this.exitZoom();
    },
  },
  beforeDestroy() {
    this.exitZoom();
  },
  methods: {
    exitZoom() {
      this.zoomed = false;
      this.lockW = 0;
      this.lockH = 0;
      this.baseW = 0;
      this.baseH = 0;
      this.bgPos = '50% 50%';
    },
    enterZoom(e) {
      if (!this.enabled || this.zoomed) return;
      const img = this.$refs.img;
      if (!img || !img.naturalWidth) return;
      const imgBox = img.getBoundingClientRect();
      const host = this.$el.parentElement || this.$el;
      const hostBox = host.getBoundingClientRect();
      // 父容器比图片大（方框上下/左右留白）时，放大视口铺满父容器
      const useHost =
        hostBox.width > imgBox.width + 1 || hostBox.height > imgBox.height + 1;
      this.lockW = useHost ? hostBox.width : imgBox.width;
      this.lockH = useHost ? hostBox.height : imgBox.height;
      this.baseW = imgBox.width;
      this.baseH = imgBox.height;
      this.zoomed = true;
      this.updatePan(e, {
        left: useHost ? hostBox.left : imgBox.left,
        top: useHost ? hostBox.top : imgBox.top,
        width: this.lockW,
        height: this.lockH,
      });
    },
    onMove(e) {
      if (!this.enabled) return;
      if (!this.zoomed) {
        this.enterZoom(e);
        return;
      }
      const box = this.$el.getBoundingClientRect();
      this.updatePan(e, box);
    },
    updatePan(e, box) {
      const w = box.width || 1;
      const h = box.height || 1;
      const x = Math.max(0, Math.min(1, (e.clientX - box.left) / w));
      const y = Math.max(0, Math.min(1, (e.clientY - box.top) / h));
      this.bgPos = `${x * 100}% ${y * 100}%`;
    },
  },
};
</script>

<style scoped lang="scss">
.br-image-side-zoom {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  max-width: 100%;
  max-height: 100%;
  line-height: 0;
  cursor: pointer;
  user-select: none;
  overflow: hidden;
  box-sizing: border-box;
  background-color: #f9f9f9;
}

.br-image-side-zoom--zoomed {
  display: block;
  cursor: pointer;
  border: 1px solid #e0e0e0;
  /* 放大后由 JS 设为父容器尺寸，在方框内铺满留白区域 */
  flex-shrink: 0;
}

.br-image-side-zoom__img {
  display: block;
  max-width: 100%;
  max-height: 100%;
}
</style>
